﻿<script lang="ts">
    import { Icon, List, ListGroup, ListItem, Toggle } from 'framework7-svelte';
    import { _ } from 'svelte-i18n';
    
    import { getLegendColorRGB, getLegendColorString } from '../../utils/style.js';
    import FloatingButton from '../FloatingButton.svelte';
    import { extractRouteCore } from '../../utils/routecore';
    import AutoComplete from '../Dashboard/AutoComplete.svelte';
    import {routeInfoStore} from "../../stores/route-info.store";
    import {projectStore} from "../../stores/project.store";
    
    export let resetRoutes: boolean = false;

    let ignoreNextToggleTimeout = undefined;
    let allRoutesVisible = false;

    $: if (resetRoutes) {
        toggleAllRoutes(true);
        resetRoutes = false;
    }

    $: {
        const visible = !Object.values($routeInfoStore).some((r) => r.hidden === true);
        if (visible !== allRoutesVisible) {
            allRoutesVisible = visible;
            // When visibility changes because of having clicked toggles, this triggers a change event which will then
            // enable/disable everything. To prevent this, we set to ignore the next toggle.
            doIgnoreNextToggle();
        }
    }
    
    function showRoute(event) {
        const id = event.target.value;
        const visible = event.target.checked;
        if (allRoutesVisible && !visible) {
            doIgnoreNextToggle();
        }
        
        routeInfoStore.set({
            ...$routeInfoStore,
            [id]: {
                ...$routeInfoStore[id],
                hidden: !visible
            }
        })
    }

    const IGNORE_TIME_MS = 250;

    // Ignore the next toggle request for IGNORE_TIME_MS milliseconds. This is a poor man's solution
    // that we use because we cannot reliably detect whether a toggle event results from an explicit click
    // on the toggle component, or implicitly because another route is selected/unselected which changes the
    // toggle value. I've spent many hours on this, and it is very difficult to get right in all cases. The
    // timeout approach is not very nice, but it is simple at least.
    function doIgnoreNextToggle() {
        if (ignoreNextToggleTimeout) {
            clearTimeout(ignoreNextToggleTimeout);
        }
        ignoreNextToggleTimeout = setTimeout(() => {
            ignoreNextToggleTimeout = undefined;
        }, IGNORE_TIME_MS);
    }

    function toggleGroupRoutes(groupRoutes: boolean) {
        const routeInfoCopy = $routeInfoStore;
        
        if (!groupRoutes) {
            let idx = 0;
            for (const route of $projectStore.data?.routes ?? []) {
                routeInfoCopy[route.id].color = getLegendColorRGB(idx++);
            }
        } else {
            let routeGroups: string[] = [];
            for (const route of $projectStore.data?.routes ?? []) {
                let routeGroup = extractRouteCore(route.name);
                if (!routeGroups?.includes(routeGroup)) {
                    routeGroups.push(routeGroup);
                }
                routeInfoCopy[route.id].color = getLegendColorRGB(routeGroups.indexOf(routeGroup));
            }
        }

        routeInfoStore.set(routeInfoCopy);
    }

    async function toggleAllRoutes(value: boolean, q: unknown) {
        if (!!ignoreNextToggleTimeout) {
            return;
        }

        const routeInfoCopy = $routeInfoStore;

        if (!value) {
            for (const route of $projectStore.data?.routes ?? []) {
                routeInfoCopy[route.id].hidden = true;
            }
        } else {
            for (const route of $projectStore.data?.routes ?? []) {
                routeInfoCopy[route.id].hidden = false;
            }
        }

        routeInfoStore.set(routeInfoCopy);
    }

    $: routes = $projectStore.data?.routes ?? [];
    const handleChangeFilter = (event) => {
        routes = $projectStore.data?.routes.filter((r) => r.name.toLowerCase().includes(event.detail.toLowerCase()));
    };

    $: active = !allRoutesVisible;
</script>

<FloatingButton label="{$_('pages.project.SELECT_ROUTES')}" {active} popoverWidth="324px" popoverHeight="505px">
    <div class="popover-content">
        <List ul={false}>
            <ListGroup>
                <ListItem title="{$_('pages.project.GROUP_ROUTES')}">
                    <Toggle slot="after" onToggleChange={(x) => toggleGroupRoutes(x)} />
                </ListItem>
                <ListItem title="{$_('shared.SHOW_ALL')}">
                    <Toggle slot="after" checked={allRoutesVisible} onToggleChange={(x, y) => toggleAllRoutes(x, y)} />
                </ListItem>
            </ListGroup>
        </List>

        <AutoComplete values={routes?.map((r) => r.name)} on:change={handleChangeFilter} />
        <div class="scroll-container">
            <div class="scroll-parent">
                <List ul={false}>
                    <ListGroup>
                        {#each routes ?? [] as route}
                            <ListItem
                                checkbox
                                checkboxIcon="end"
                                value={route.id}
                                title={route.name}
                                onChange={showRoute}
                                checked={$routeInfoStore[route.id]?.hidden !== true}
                            >
                                <Icon f7="circle_fill" style={`color: ${getLegendColorString($routeInfoStore[route.id]?.color)};font-size: 12pt;`} slot="media" />
                            </ListItem>
                        {/each}
                    </ListGroup>
                </List>
            </div>
        </div>
    </div>
</FloatingButton>

<style lang="scss">
    .popover-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .scroll-parent {
        display: flex;
        flex-flow: column;
        height: 100%;
    }

    .scroll-container {
        overflow: auto;
        flex-grow: 1;
    }
</style>
